import React, { useState, useEffect } from 'react';
import axios, { AxiosResponse } from 'axios';
import 'bootstrap/dist/css/bootstrap.min.css';
import { Form, Button, Row, Col, Alert } from 'react-bootstrap';
import { Navigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../Redux/store';
import { translations } from '../utils/translations';

interface Category {
  id: number;
  name: string;
}

interface Supplier {
  id: number;
  name: string;
}

export default function AddProductComponent() {
  const language = useSelector((state: RootState) => state.language.language);
  const t = translations[language as keyof typeof translations];
  const [categories, setCategories] = useState<Category[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [name, setName] = useState<string>('');
  const [category, setCategory] = useState<number>(0);
  const [supplier, setSupplier] = useState<number>(0);
  const [description, setDescription] = useState<string>('');
  const [branch] = useState<number>(1);
  const [price, setPrice] = useState<string>('');
  const [costPrice, setCostPrice] = useState<string>('');
  const [quantityInStock, setQuantityInStock] = useState<number>(1);
  const [barcode, setBarcode] = useState<string>('');
  const [unitOfMeasurement, setUnitOfMeasurement] = useState<string>('pcs');
  const [image, setImage] = useState<File | null>(null);
  const [status, setStatus] = useState<string>('Active');
  const [dateAdded, setDateAdded] = useState<string>(new Date().toISOString().split('T')[0]);
  const [redirect, setRedirect] = useState<boolean>(false);
  const [showCostPriceError, setShowCostPriceError] = useState<boolean>(false);

  // Helper to fetch all paginated data
  async function fetchAllPages<T>(url: string): Promise<T[]> {
    let allItems: T[] = [];
    let nextUrl: string | null = url;

    while (nextUrl) {
      const response: AxiosResponse<{ results: T[]; next: string | null }> =
        await axios.get<{ results: T[]; next: string | null }>(nextUrl);
      allItems = allItems.concat(response.data.results);
      nextUrl = response.data.next;
    }

    return allItems;
  }

  const fetchCategories = async () => {
    try {
      const allCats = await fetchAllPages<Category>('https://api.sominnovations.xyz/Branches/categories/');
      setCategories(allCats);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchSuppliers = async () => {
    try {
      const allSups = await fetchAllPages<Supplier>('https://api.sominnovations.xyz/Branches/suppliers/');
      setSuppliers(allSups);
    } catch (error) {
      console.error('Error fetching suppliers:', error);
    }
  };

  useEffect(() => {
    fetchCategories();
    fetchSuppliers();
  }, []);

  useEffect(() => {
    const priceNum = parseFloat(price);
    const costPriceNum = parseFloat(costPrice);
    if (isNaN(costPriceNum)) return;
    setShowCostPriceError(costPriceNum > priceNum);
  }, [price, costPrice]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const priceNum = parseFloat(price);
    const costPriceNum = parseFloat(costPrice);
    if (costPriceNum > priceNum) {
      alert('Cost price cannot be higher than selling price!');
      return;
    }

    const formData = new FormData();
    formData.append('name', name);
    formData.append('category', String(category));
    formData.append('supplier', String(supplier));
    formData.append('description', description);
    formData.append('branch', String(branch));
    formData.append('price', price);
    formData.append('cost_price', costPrice);
    formData.append('quantity_in_stock', String(quantityInStock));
    formData.append('barcode', barcode);
    formData.append('unit_of_measurement', unitOfMeasurement);
    formData.append('date_added', dateAdded);
    if (image) formData.append('image', image);
    formData.append('status', status);

    try {
      await axios.post('https://api.sominnovations.xyz/Branches/products/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      alert('Product added successfully!');
      setRedirect(true);
    } catch (error: any) {
      if (error.response) console.log('Error response data:', error.response.data);
      else console.error('Error', error.message);
    }
  };

  if (redirect) return <Navigate to="/products" />;

  return (
    <div className="container mt-1" style={{ maxWidth: '800px', padding: '20px', border: '1px solid #ccc', borderRadius: '8px' }}>
      <h2 className="text-center mb-4">{t.addProduct}</h2>
      <Form onSubmit={handleSubmit}>
        <Row className="mb-3">
          <Col md={6}>
            <Form.Group controlId="formName">
              <Form.Label>Name</Form.Label>
              <Form.Control type="text" value={name} onChange={e => setName(e.target.value)} required />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group controlId="formCategory">
              <Form.Label>Category</Form.Label>
              <Form.Control as="select" onChange={e => setCategory(Number(e.target.value))} required>
                <option value="">Select Category</option>
                {categories.map(cat => (
                  <option key={cat.id} value={cat.id}>{cat.name}</option>
                ))}
              </Form.Control>
            </Form.Group>
          </Col>
        </Row>

        <Row className="mb-3">
          <Col md={6}>
            <Form.Group controlId="formSuppliers">
              <Form.Label>Supplier</Form.Label>
              <Form.Control as="select" onChange={e => setSupplier(Number(e.target.value))} required>
                <option value="">Select Supplier</option>
                {suppliers.map(sup => (
                  <option key={sup.id} value={sup.id}>{sup.name}</option>
                ))}
              </Form.Control>
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group controlId="formPrice">
              <Form.Label>{t.sellingPrice}</Form.Label>
              <Form.Control type="number" value={price} onChange={e => setPrice(e.target.value)} required min="0" step="0.01" isInvalid={showCostPriceError} />
              {showCostPriceError && <Form.Control.Feedback type="invalid">Selling price must be higher than cost price</Form.Control.Feedback>}
            </Form.Group>
          </Col>
        </Row>

        <Row className="mb-3">
          <Col md={6}>
            <Form.Group controlId="formCostPrice">
              <Form.Label>{t.costPrice}</Form.Label>
              <Form.Control type="number" value={costPrice} onChange={e => setCostPrice(e.target.value)} required min="0" step="0.01" isInvalid={showCostPriceError} />
              {showCostPriceError && <Form.Control.Feedback type="invalid">Cost price cannot be higher than selling price</Form.Control.Feedback>}
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group controlId="formQuantity">
              <Form.Label>{t.quantityInStock}</Form.Label>
              <Form.Control type="number" value={quantityInStock} onChange={e => setQuantityInStock(Number(e.target.value))} min="1" required />
            </Form.Group>
          </Col>
        </Row>

        <Row className="mb-3">
          <Col md={6}>
            <Form.Group controlId="formBarcode">
              <Form.Label>Barcode</Form.Label>
              <Form.Control type="text" value={barcode} onChange={e => setBarcode(e.target.value)} />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group controlId="formUnitOfMeasurement">
              <Form.Label>Unit of Measurement</Form.Label>
              <Form.Control type="text" value={unitOfMeasurement} onChange={e => setUnitOfMeasurement(e.target.value)} />
            </Form.Group>
          </Col>
        </Row>

        <Row className="mb-3">
          <Col md={6}>
            <Form.Group controlId="formDateAdded">
              <Form.Label>Date Added</Form.Label>
              <Form.Control type="date" value={dateAdded} onChange={e => setDateAdded(e.target.value)} required />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group controlId="formImage">
              <Form.Label>Product Image</Form.Label>
              <Form.Control
                type="file"
                accept="image/*"
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setImage(e.target.files?.[0] || null)}
              />
            </Form.Group>
          </Col>
        </Row>

        <Form.Group controlId="formStatus" className="mb-4">
          <Form.Label>Status</Form.Label>
          <Form.Control as="select" value={status} onChange={e => setStatus(e.target.value)}>
            <option value="Active">Active</option>
            <option value="Inactive">Inactive</option>
          </Form.Control>
        </Form.Group>

        {showCostPriceError && (
          <Alert variant="danger" className="mb-3">
            Warning: The cost price is higher than the selling price. This will result in a loss.
          </Alert>
        )}

        <Button variant="primary" type="submit" className="w-100">
          {t.addProduct}
        </Button>
      </Form>
    </div>
  );
}
